/**
 * 編集 Sheet — 選択セクションのフィールドをインライン編集。
 * blur 時に Firestore へ部分更新。
 */
import { SECTIONS, type SectionStyle, type SectionType } from '@goenhub/landing-sections';
import { AlignCenter, AlignLeft, AlignRight, Loader2, Pencil, Sparkles, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { ArrayFieldEditor } from './ArrayFieldEditor';
import { CtaHrefField } from './CtaHrefField';
import { ImageFieldEditor } from './ImageFieldEditor';
import { SectionStatusPill, useUpdateSectionRef } from './SectionStatusPill';

import { Button } from '@/components/ui/button';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import {
  useLandingSection,
  useRegenerateLandingSection,
  useUpdateLandingSection,
  type LandingRecord,
} from '@/hooks/useLandings';

interface Props {
  landingId: string;
  landing: LandingRecord;
  sectionId: string;
  onClose: () => void;
}

export function SectionEditorSheet({ landingId, landing, sectionId, onClose }: Props) {
  const { data: section, isLoading } = useLandingSection(landingId, sectionId);
  const updateSection = useUpdateLandingSection();
  const regenSection = useRegenerateLandingSection();
  const [instruction, setInstruction] = useState('');

  const sectionRef = landing.sections.find((s) => s.id === sectionId);
  const updateSectionRef = useUpdateSectionRef(landingId, landing.sections);
  const sectionDef = sectionRef ? SECTIONS[sectionRef.type as SectionType] : null;

  const handleFieldUpdate = async (key: string, value: unknown) => {
    await updateSection.mutateAsync({
      landingId,
      sectionId,
      patch: { contentPatch: { [key]: value } },
    });
  };

  const handleStyleUpdate = async (key: keyof SectionStyle, value: unknown) => {
    await updateSection.mutateAsync({
      landingId,
      sectionId,
      patch: { stylePatch: { [key]: value } },
    });
  };

  const [useCurrentContent, setUseCurrentContent] = useState(true);
  const handleRegen = async () => {
    try {
      await regenSection.mutateAsync({
        landingId,
        sectionId,
        instruction: instruction.trim() || undefined,
        useCurrentContent,
      });
      toast.success('再生成完了');
      setInstruction('');
    } catch (e) {
      toast.error(`再生成失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // backdrop クリックで閉じる際、テキストドラッグ選択（mousedown 内部 → mouseup 外部）
  // を「外側クリック」と誤認しないよう、mousedown 起点が backdrop 自身であるかを記録する。
  const downOnBackdropRef = useRef(false);

  return (
    <div
      className="fixed inset-0 bg-black/30 z-40 flex justify-end"
      onMouseDown={(e) => {
        downOnBackdropRef.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (e.target !== e.currentTarget) return;
        if (!downOnBackdropRef.current) return;
        onClose();
      }}
    >
      <div
        className="w-[480px] h-full bg-[var(--bg-elevated)] border-l border-[var(--border)] overflow-y-auto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 p-3 border-b border-[var(--border)] sticky top-0 bg-[var(--bg-elevated)] z-10">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Pencil className="size-4 text-[var(--text-muted)] flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-wide font-semibold text-[var(--text-muted)]">
                EDIT · {sectionRef?.type}
              </div>
              {sectionRef && (
                <SectionTitleEditor
                  key={sectionRef.id}
                  title={sectionRef.title ?? ''}
                  fallback={sectionRef.type}
                  onSave={(title) => updateSectionRef(sectionRef.id, { title, titleManual: true })}
                />
              )}
            </div>
          </div>
          {sectionRef && (
            <SectionStatusPill
              landingId={landingId}
              sections={landing.sections}
              section={sectionRef}
              size="md"
            />
          )}
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="size-4" />
          </Button>
        </div>

        <div className="p-3">
          <Tabs defaultValue="content">
            <TabsList className="w-full">
              <TabsTrigger value="content" className="flex-1">
                内容
              </TabsTrigger>
              <TabsTrigger value="style" className="flex-1">
                スタイル
              </TabsTrigger>
              <TabsTrigger value="ai" className="flex-1">
                AI 最適化
              </TabsTrigger>
            </TabsList>

            <TabsContent value="content" className="mt-3 space-y-3">
              {isLoading || !section ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="size-4 animate-spin" />
                </div>
              ) : sectionDef ? (
                <>
                  <FieldsRenderer
                    landingId={landingId}
                    uiSchema={
                      sectionDef.uiSchema as Array<{
                        name: string;
                        type: string;
                        label: string;
                        max?: number;
                        placeholder?: string;
                      }>
                    }
                    content={section.content}
                    onUpdate={handleFieldUpdate}
                  />
                  {sectionDef.hasCta && (
                    <CtaAlignField
                      value={section.style?.ctaAlign}
                      onChange={(v) => handleStyleUpdate('ctaAlign', v)}
                    />
                  )}
                  {sectionRef?.type === 'hero' && (
                    <HeroLogoFields
                      logoUrl={landing.brandSettings?.logoStoragePath}
                      position={section.style?.logoPosition}
                      size={section.style?.logoSize}
                      onChangePosition={(v) => handleStyleUpdate('logoPosition', v)}
                      onChangeSize={(v) => handleStyleUpdate('logoSize', v)}
                    />
                  )}
                </>
              ) : (
                <div className="text-xs text-[var(--text-muted)]">不明なセクションタイプ</div>
              )}
            </TabsContent>

            <TabsContent value="style" className="mt-3">
              <VariantSwitcher landingId={landingId} landing={landing} sectionId={sectionId} />
            </TabsContent>

            <TabsContent value="ai" className="mt-3 space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs">追加指示 (任意)</Label>
                <Textarea
                  value={instruction}
                  onChange={(e) => setInstruction(e.target.value)}
                  placeholder="例: もっとカジュアルに / 英語で / 数字を強調 / B2B 寄りの表現に"
                  rows={3}
                />
                <p className="text-[10px] text-[var(--text-muted)]">
                  ブランド情報・キャンペーン情報は自動的にプロンプトに含まれます。
                </p>
              </div>

              <label
                htmlFor="regen-use-current-tab"
                className="flex items-start gap-2 p-3 rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] cursor-pointer"
              >
                <input
                  id="regen-use-current-tab"
                  type="checkbox"
                  checked={useCurrentContent}
                  onChange={(e) => setUseCurrentContent(e.target.checked)}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <div className="text-xs font-semibold">現在の内容を踏襲する (推奨)</div>
                  <div className="text-[10px] text-[var(--text-muted)] mt-1 leading-relaxed">
                    {useCurrentContent
                      ? '現在のセクション内容を AI に渡し、トーンや構成を保ったまま書き直します。'
                      : 'ブランド + キャンペーン情報だけからゼロベースで再生成します。現在の内容は破棄されます。'}
                  </div>
                </div>
              </label>

              <Button onClick={handleRegen} disabled={regenSection.isPending} className="w-full">
                {regenSection.isPending ? (
                  <>
                    <Loader2 className="size-3.5 mr-1 animate-spin" /> 再生成中...
                  </>
                ) : (
                  <>
                    <Sparkles className="size-3.5 mr-1" /> AI で書き直す
                  </>
                )}
              </Button>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

function VariantSwitcher({
  landingId,
  landing,
  sectionId,
}: {
  landingId: string;
  landing: LandingRecord;
  sectionId: string;
}) {
  const updateSection = useUpdateLandingSection();
  const updateSectionRef = useUpdateSectionRef(landingId, landing.sections);
  const [saving, setSaving] = useState<string | null>(null);
  const sectionRef = landing.sections.find((s) => s.id === sectionId);
  if (!sectionRef) return null;
  const sectionDef = SECTIONS[sectionRef.type as SectionType];
  if (!sectionDef) return null;
  const variants = Object.keys(sectionDef.variants);

  // バリアントは 2 か所に持っている:
  //   landing.sections[].variant … プレビュー / 公開ページ / 一覧 / 選択表示が読む
  //   sections/{id}.variant      … AI 再生成・AI 複製が読む
  // 片方だけ書くと画面が変わらない（2026-09-25 修正）ため、両方を書く。
  const handleSelect = async (v: string) => {
    if (saving) return;
    setSaving(v);
    try {
      await Promise.all([
        updateSectionRef(sectionId, { variant: v }),
        updateSection.mutateAsync({ landingId, sectionId, patch: { variant: v } }),
      ]);
    } catch (e) {
      toast.error(`バリアントの変更に失敗しました: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(null);
    }
  };
  return (
    <div className="space-y-2">
      <Label className="text-xs">バリアント</Label>
      <div className="grid grid-cols-2 gap-1.5">
        {variants.map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => void handleSelect(v)}
            disabled={saving !== null}
            className={`px-3 py-2 text-xs rounded-md border disabled:opacity-60 ${
              v === sectionRef.variant
                ? 'border-[var(--accent)] bg-[var(--accent-subtle)]'
                : 'border-[var(--border)] hover:bg-[var(--bg-hover)]'
            }`}
          >
            {saving === v ? <Loader2 className="size-3 inline animate-spin mr-1" /> : null}
            {v}
          </button>
        ))}
      </div>
    </div>
  );
}

interface FieldsRendererProps {
  landingId: string;
  uiSchema: Array<{
    name: string;
    type: string;
    label: string;
    max?: number;
    placeholder?: string;
  }>;
  content: Record<string, unknown>;
  onUpdate: (key: string, value: unknown) => Promise<void> | void;
}

function FieldsRenderer({ landingId, uiSchema, content, onUpdate }: FieldsRendererProps) {
  return (
    <div className="space-y-3">
      {uiSchema.map((f) => (
        <FieldRow
          key={f.name}
          field={f}
          value={content[f.name]}
          landingId={landingId}
          onUpdate={onUpdate}
        />
      ))}
    </div>
  );
}

const ARRAY_FIELD_TYPES = new Set([
  'product_list',
  'feature_list',
  'qa_list',
  'logo_list',
  'testimonial_list',
  'plan_list',
  'video_list',
  'team_list',
  'stat_list',
  'step_list',
  'comparison_rows',
  'footer_columns',
  'spotlight_blocks',
]);

function FieldRow({
  field,
  value,
  landingId,
  onUpdate,
}: {
  field: {
    name: string;
    type: string;
    label: string;
    max?: number;
    placeholder?: string;
    hint?: string;
    options?: Array<{ value: string; label: string }>;
  };
  value: unknown;
  landingId: string;
  onUpdate: (key: string, value: unknown) => Promise<void> | void;
}) {
  if (field.type === 'text' || field.type === 'textarea') {
    return (
      <div>
        <Label className="text-xs">{field.label}</Label>
        <BlurField
          value={typeof value === 'string' ? value : ''}
          multiline={field.type === 'textarea'}
          placeholder={field.placeholder}
          onSave={(v) => onUpdate(field.name, v)}
        />
      </div>
    );
  }
  if (field.type === 'cta_url') {
    return (
      <CtaHrefField
        label={field.label}
        value={typeof value === 'string' ? value : ''}
        onChange={(v) => onUpdate(field.name, v)}
      />
    );
  }
  if (field.type === 'datetime') {
    return (
      <DateTimeField
        label={field.label}
        value={typeof value === 'string' ? value : ''}
        hint={field.hint}
        onChange={(v) => onUpdate(field.name, v)}
      />
    );
  }
  if (field.type === 'image') {
    return (
      <ImageFieldEditor
        label={field.label}
        value={typeof value === 'string' ? value : null}
        onChange={(v) => onUpdate(field.name, v)}
        storagePath="marketing/lp/imgs"
      />
    );
  }
  if (field.type === 'image_list') {
    const arr = Array.isArray(value) ? (value as string[]) : [];
    return (
      <div>
        <Label className="text-xs">{field.label}</Label>
        <div className="space-y-2">
          {arr.map((url, i) => (
            <ImageFieldEditor
              key={i}
              label={`#${i + 1}`}
              value={url}
              onChange={(v) => {
                const next = arr.slice();
                if (v) next[i] = v;
                else next.splice(i, 1);
                onUpdate(field.name, next);
              }}
              storagePath="marketing/lp/imgs"
            />
          ))}
          <button
            type="button"
            onClick={() => onUpdate(field.name, [...arr, ''])}
            className="w-full text-xs text-[var(--text-muted)] border border-dashed border-[var(--border)] rounded-md py-2 hover:bg-[var(--bg-hover)]"
          >
            + 画像を追加
          </button>
        </div>
      </div>
    );
  }
  if (field.type === 'select') {
    const opts = field.options ?? [];
    return (
      <div>
        <Label className="text-xs">{field.label}</Label>
        <select
          value={typeof value === 'string' ? value : (opts[0]?.value ?? '')}
          onChange={(e) => onUpdate(field.name, e.target.value)}
          className="w-full px-3 py-2 text-sm bg-[var(--bg-input)] border border-[var(--border)] rounded-sm"
        >
          {opts.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    );
  }
  if (ARRAY_FIELD_TYPES.has(field.type)) {
    const arr = Array.isArray(value) ? (value as Array<Record<string, unknown>>) : [];
    return (
      <div>
        <Label className="text-xs">{field.label}</Label>
        <ArrayFieldEditor
          fieldType={field.type}
          items={arr}
          onChange={(next) => onUpdate(field.name, next)}
          landingId={landingId}
        />
      </div>
    );
  }
  return null;
}

function BlurField({
  value,
  multiline,
  placeholder,
  rows = 2,
  mono,
  onSave,
}: {
  value: string;
  multiline?: boolean;
  placeholder?: string;
  rows?: number;
  mono?: boolean;
  onSave: (v: string) => Promise<unknown> | void;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  const composing = useRef(false);
  const handleBlur = () => {
    if (composing.current) return;
    if (local === value) return;
    void onSave(local);
  };
  if (multiline) {
    return (
      <Textarea
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={handleBlur}
        onCompositionStart={() => (composing.current = true)}
        onCompositionEnd={() => (composing.current = false)}
        placeholder={placeholder}
        rows={rows}
        className={mono ? 'font-mono text-[11px]' : ''}
      />
    );
  }
  return (
    <Input
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={handleBlur}
      onCompositionStart={() => (composing.current = true)}
      onCompositionEnd={() => (composing.current = false)}
      placeholder={placeholder}
    />
  );
}

function CtaAlignField({
  value,
  onChange,
}: {
  value: SectionStyle['ctaAlign'] | undefined;
  onChange: (v: SectionStyle['ctaAlign']) => void;
}) {
  const options: Array<{
    key: NonNullable<SectionStyle['ctaAlign']>;
    icon: React.ReactNode;
    label: string;
  }> = [
    { key: 'left', icon: <AlignLeft className="size-3" />, label: '左' },
    { key: 'center', icon: <AlignCenter className="size-3" />, label: '中央' },
    { key: 'right', icon: <AlignRight className="size-3" />, label: '右' },
  ];
  return (
    <div className="pt-2 border-t border-[var(--border)]">
      <Label className="text-xs">CTA ボタン位置</Label>
      <div className="mt-1 inline-flex border border-[var(--border)] rounded-md overflow-hidden text-[11px]">
        {options.map((opt) => {
          const active = value === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => onChange(opt.key)}
              className={`flex items-center gap-1 px-3 py-1.5 transition-colors ${
                active
                  ? 'bg-[var(--accent)] text-white'
                  : 'text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              {opt.icon}
              <span>{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function HeroLogoFields({
  logoUrl,
  position,
  size,
  onChangePosition,
  onChangeSize,
}: {
  logoUrl: string | null | undefined;
  position: SectionStyle['logoPosition'];
  size: SectionStyle['logoSize'];
  onChangePosition: (v: NonNullable<SectionStyle['logoPosition']>) => void;
  onChangeSize: (v: NonNullable<SectionStyle['logoSize']>) => void;
}) {
  const positionOptions: Array<{ key: NonNullable<SectionStyle['logoPosition']>; label: string }> =
    [
      { key: 'top-left', label: '上 左' },
      { key: 'top-center', label: '上 中央' },
      { key: 'top-right', label: '上 右' },
      { key: 'bottom-left', label: '下 左' },
      { key: 'bottom-right', label: '下 右' },
      { key: 'none', label: '非表示' },
    ];
  const sizeOptions: Array<{ key: NonNullable<SectionStyle['logoSize']>; label: string }> = [
    { key: 'sm', label: 'S' },
    { key: 'md', label: 'M' },
    { key: 'lg', label: 'L' },
  ];
  const effectivePosition = position ?? 'top-left';
  const effectiveSize = size ?? 'md';
  return (
    <div className="pt-2 border-t border-[var(--border)] space-y-2">
      <div>
        <Label className="text-xs">ロゴ表示</Label>
        {!logoUrl && (
          <div className="text-[10px] text-[var(--text-muted)] mt-0.5">
            ※ ロゴ画像は Brand タブで設定してください
          </div>
        )}
        <div className="mt-1 grid grid-cols-3 gap-1 text-[11px]">
          {positionOptions.map((opt) => {
            const active = effectivePosition === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => onChangePosition(opt.key)}
                className={`px-2 py-1.5 rounded-md border transition-colors ${
                  active
                    ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]'
                    : 'border-[var(--border)] text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <Label className="text-xs">ロゴサイズ</Label>
        <div className="mt-1 inline-flex border border-[var(--border)] rounded-md overflow-hidden text-[11px]">
          {sizeOptions.map((opt) => {
            const active = effectiveSize === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => onChangeSize(opt.key)}
                className={`px-3 py-1.5 transition-colors ${
                  active
                    ? 'bg-[var(--accent)] text-white'
                    : 'text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * 日時入力フィールド (datetime-local + ISO 8601 with JST offset)
 *
 * 値はストア上では ISO 8601 (例: "2026-06-16T14:00:00+09:00") で保存。
 * 表示時は datetime-local 形式 ("2026-06-16T14:00") に変換。
 * blur 時に JST (+09:00) 想定の ISO へ再構築して保存。
 * 空入力 → 空文字を保存（空欄を許容）。
 */
function DateTimeField({
  label,
  value,
  hint,
  onChange,
}: {
  label: string;
  value: string;
  hint?: string;
  onChange: (v: string) => void;
}) {
  // ストア値 (ISO) → datetime-local input 値 (YYYY-MM-DDTHH:MM)
  const toLocalInput = (iso: string): string => {
    if (!iso) return '';
    // 既に datetime-local 形式ならそのまま
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(iso)) return iso;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    // JST 表示にするため getTime + 9h 補正して YYYY-MM-DDTHH:MM 抽出
    const jstMs = d.getTime() + 9 * 60 * 60 * 1000;
    const j = new Date(jstMs);
    const yyyy = j.getUTCFullYear();
    const mm = String(j.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(j.getUTCDate()).padStart(2, '0');
    const hh = String(j.getUTCHours()).padStart(2, '0');
    const mi = String(j.getUTCMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  };

  // datetime-local 入力値 → ストア値 (ISO 8601 with +09:00)
  const toIso = (local: string): string => {
    if (!local) return '';
    // YYYY-MM-DDTHH:MM
    return `${local}:00+09:00`;
  };

  const [local, setLocal] = useState(() => toLocalInput(value));
  useEffect(() => setLocal(toLocalInput(value)), [value]);

  const handleChange = (next: string) => {
    setLocal(next);
    // 即時保存（blur を待たずに反映）— カウントダウンのライブプレビュー用
    onChange(next ? toIso(next) : '');
  };

  // 現在値の表示プレビュー (例: "2026/06/16 14:00 JST")
  const previewText = useMemo(() => {
    if (!local) return '日時未設定';
    const [datePart, timePart] = local.split('T');
    return `${datePart.replace(/-/g, '/')} ${timePart} (JST)`;
  }, [local]);

  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input
        type="datetime-local"
        value={local}
        onChange={(e) => handleChange(e.target.value)}
        className="font-mono"
      />
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] text-[var(--text-muted)]">{previewText}</span>
        {local && (
          <button
            type="button"
            onClick={() => handleChange('')}
            className="text-[10px] text-[var(--text-muted)] hover:text-red-500"
          >
            クリア
          </button>
        )}
      </div>
      {hint && <p className="text-[10px] text-[var(--text-muted)]">{hint}</p>}
    </div>
  );
}

/**
 * 編集エリア上部のセクション名。クリックで入力欄になり、Enter / 欄外クリックで保存、Esc で取り消し。
 * 保存した名前は titleManual=true となり、AI 再生成・AI 複製で上書きされない。
 */
function SectionTitleEditor({
  title,
  fallback,
  onSave,
}: {
  title: string;
  fallback: string;
  onSave: (title: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const [saving, setSaving] = useState(false);
  const cancelledRef = useRef(false);

  const start = () => {
    setDraft(title);
    cancelledRef.current = false;
    setEditing(true);
  };

  const commit = async () => {
    if (cancelledRef.current) return;
    setEditing(false);
    const next = draft.trim();
    // 空欄は保存せず元の名前に戻す
    if (!next || next === title) return;
    setSaving(true);
    try {
      await onSave(next);
    } catch (e) {
      toast.error(`名前の保存に失敗しました: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={(e) => e.currentTarget.select()}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          // IME 変換確定の Enter では保存しない
          if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
            e.preventDefault();
            e.currentTarget.blur();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            cancelledRef.current = true;
            setEditing(false);
          }
        }}
        aria-label="セクション名"
        className="w-full text-sm font-medium bg-[var(--bg-surface)] border border-[var(--accent)] rounded px-1.5 py-0.5 outline-none"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      title="クリックで名前を変更"
      className="group flex items-center gap-1 max-w-full text-left rounded px-1.5 py-0.5 -mx-1.5 hover:bg-[var(--bg-hover)]"
    >
      <span className="text-sm font-medium truncate">{title || fallback}</span>
      {saving ? (
        <Loader2 className="size-3 animate-spin text-[var(--text-muted)] flex-shrink-0" />
      ) : (
        <Pencil className="size-3 text-[var(--text-muted)] opacity-0 group-hover:opacity-100 flex-shrink-0" />
      )}
    </button>
  );
}
